/* eslint-disable import/no-extraneous-dependencies */

import PIv3Client, { PISdkBuilder } from "@ws-usage/pi3-typescript";
import { defaultTrackingDataConfig, getPiDomain } from "./config";
import type { IUsageTracking, TrackingEvent } from "./tracking.types";
import accessTokenService from "../services/accessTokenService";
import runtimeConfigService from "../config/runtimeConfig";

export class UsageTracking implements IUsageTracking {
  public isInitialized = false;

  private static instance: UsageTracking;

  private piSdk!: PIv3Client;

  private _eventData!: TrackingEvent;

  private _backendVersion?: string;

  private _armVersion?: string;

  private listeners: Array<(value: boolean) => void> = [];

  static get Instance(): UsageTracking {
    if (!this.instance) {
      this.instance = new this();
    }

    return this.instance;
  }

  subscribeInitialized(listener: (value: boolean) => void) {
    this.listeners.push(listener);
    listener(this.isInitialized);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notifyInitialized() {
    this.listeners.forEach((listener) => listener(this.isInitialized));
  }

  async initialize() {
    await runtimeConfigService.loadConfig();
    this.piSdk = PISdkBuilder.Instance.setIngestionUrls([
      getPiDomain(runtimeConfigService.getConfig().apiUrl),
    ]).build();
    this.setDefaultConfig();
    this.isInitialized = true;
    this.notifyInitialized();
  }

  setEventName(eventName: string) {
    if (!this._eventData) {
      this.setDefaultConfig();
    }
    this._eventData.eventName = eventName;
    return this;
  }

  setPiiData(piiData: Record<string, unknown>) {
    if (!this._eventData) {
      this.setDefaultConfig();
    }
    this._eventData.piiData = {
      ...this._eventData.piiData,
      ...piiData,
    };
    return this;
  }

  setNonPiiData(nonPiiData: Record<string, unknown>) {
    if (!this._eventData) {
      this.setDefaultConfig();
    }
    this._eventData.nonPiiData = {
      ...this._eventData.nonPiiData,
      ...nonPiiData,
    };
    return this;
  }

  setVersionInfo(backendVersion: string, armVersion: string) {
    this._backendVersion = backendVersion;
    this._armVersion = armVersion;
    if (this._eventData) {
      this._eventData.nonPiiData = {
        ...this._eventData.nonPiiData,
        "backend-version": backendVersion,
        "arm-version": armVersion,
      };
    }
    return this;
  }

  private setDefaultConfig() {
    this._eventData = {
      ...defaultTrackingDataConfig,
      userId: accessTokenService.decodedAccessToken?.oid ?? "",
      piiData: {
        ...defaultTrackingDataConfig.piiData,
        tid: accessTokenService.decodedAccessToken?.tid ?? "",
        oid: accessTokenService.decodedAccessToken?.oid ?? "",
        wids: accessTokenService.decodedAccessToken?.wids ?? "",
      },
      nonPiiData: {
        ...defaultTrackingDataConfig.nonPiiData,
        "app-id": accessTokenService.decodedAccessToken?.appid ?? "",
        ...(this._backendVersion && { "backend-version": this._backendVersion }),
        ...(this._armVersion && { "arm-version": this._armVersion }),
      },
    };
  }

  get eventData() {
    return this._eventData;
  }

  async eventTracker() {
    try {
      await this.piSdk.queueEvent({
        ...this._eventData,
        piiData: {
          ...this._eventData.piiData,
        },
      });
      // Reset once flush completed
      this.setDefaultConfig();
      // Force ingest data
      await this.piSdk.flush();
    } catch (error) {
      console.error("Queue event fail: ", error);
    }
  }
}

export default UsageTracking;
